import { getEventBadge, getEventDetail, isCanonicalOwnGoal, isKeyEvent } from '@/components/wyscout/MatchEvents/helpers';
import type { MatchEvent } from '@/components/wyscout/MatchEvents/types';
import type { EventCategory } from './types';

const normalizedTypes = (event: MatchEvent): { primary: string; secondary: string[] } => ({
  primary: event.type.primary.toLowerCase(),
  secondary: (event.type.secondary || []).map(value => value.toLowerCase()),
});

export const isGoalEvent = (event: MatchEvent): boolean =>
  isCanonicalOwnGoal(event) || event.shot?.isGoal === true;

export const isShotEvent = (event: MatchEvent): boolean => {
  const { primary, secondary } = normalizedTypes(event);
  return primary === 'shot' || primary === 'penalty' || secondary.includes('penalty') || event.shot !== undefined;
};

export const isCardEvent = (event: MatchEvent): boolean => {
  const { secondary } = normalizedTypes(event);
  return event.infraction?.yellowCard === true
    || event.infraction?.redCard === true
    || secondary.includes('yellow_card')
    || secondary.includes('red_card');
};

export const isChanceEvent = (event: MatchEvent): boolean => {
  const { primary, secondary } = normalizedTypes(event);
  return isShotEvent(event)
    || primary === 'assist'
    || primary === 'key_pass'
    || secondary.includes('assist')
    || secondary.includes('key_pass');
};

export const eventCategoryMatches = (event: MatchEvent, category: EventCategory): boolean => {
  if (category === 'all') return true;
  if (category === 'key') return isKeyEvent(event);
  if (category === 'goals') return isGoalEvent(event);
  if (category === 'shots') return isShotEvent(event);
  if (category === 'cards') return isCardEvent(event);

  return isChanceEvent(event);
};

const clampCoordinate = (value: number): number => Math.min(100, Math.max(0, value));

export const orientEventLocation = (event: MatchEvent, awayTeamId: number): { x: number; y: number } | null => {
  if (!event.location || !Number.isFinite(event.location.x) || !Number.isFinite(event.location.y)) return null;

  const x = clampCoordinate(event.location.x);
  const y = clampCoordinate(event.location.y);
  return event.team?.id === awayTeamId ? { x: 100 - x, y: 100 - y } : { x, y };
};

export const eventChanceDetail = (event: MatchEvent): 'Assist' | 'Key pass' | null => {
  if (isShotEvent(event)) return null;

  const { primary, secondary } = normalizedTypes(event);
  if (primary === 'assist' || secondary.includes('assist')) return 'Assist';
  if (primary === 'key_pass' || secondary.includes('key_pass')) return 'Key pass';
  return null;
};

export const eventActionLabel = (event: MatchEvent): string =>
  eventChanceDetail(event) ? 'Key chance' : getEventBadge(event).label;

export const eventAccessibleLabel = (event: MatchEvent, teamName: string): string => {
  const action = eventActionLabel(event);
  const chanceDetail = eventChanceDetail(event);
  const player = event.player?.name || 'Unknown player';
  return `${event.minute}' ${action}${chanceDetail ? `, ${chanceDetail}` : ''}, ${player}, ${teamName}, outcome: ${eventOutcome(event)}`;
};

export const eventOutcome = (event: MatchEvent): string => {
  if (isGoalEvent(event)) return 'Goal';
  const { secondary } = normalizedTypes(event);
  if (isShotEvent(event)) {
    if (event.shot?.onTarget) return 'Shot on target';
    if (secondary.includes('shot_block')) return 'Shot blocked';
    if (secondary.includes('hit_post')) return 'Shot hit post';
    if (secondary.includes('wide')) return 'Shot wide';
    return 'Shot';
  }

  if (event.infraction?.redCard || secondary.includes('red_card')) return 'Red card';
  if (event.infraction?.yellowCard || secondary.includes('yellow_card')) return 'Yellow card';
  if (event.pass) return event.pass.accurate ? 'Pass complete' : 'Pass incomplete';
  return getEventDetail(event) || 'Event recorded';
};

export const sortEventsChronologically = (events: MatchEvent[]): MatchEvent[] => [...events].sort((left, right) =>
  left.minute - right.minute || left.second - right.second || left.id - right.id,
);

export const goalBeneficiaryTeamId = (
  event: MatchEvent,
  homeTeamId: number,
  awayTeamId: number,
): number | null => {
  if (event.team?.id !== homeTeamId && event.team?.id !== awayTeamId) return null;

  if (!isCanonicalOwnGoal(event)) return event.team.id;
  return event.team.id === homeTeamId ? awayTeamId : homeTeamId;
};

export const displayEventValue = (value: string): string => value.replace(/_/g, ' ');
